import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { CountriesType } from 'entities/distribution/types';
import { InfoAboutUpload } from 'entities/users/types';
import { useGetCountriesQuery } from 'features/getDistributions/useGetDistributions';
import {
  useConfirmUploadShopDotsListExcelMutation,
  useUploadShopDotsListExcelMutation,
} from 'features/uploadShopDots/uploadShopDotsExcelApi';
import { isEmpty } from 'lodash';
import { FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BASE_URL } from 'shared/conf/constants';
import { CountrySelectId, requiredErrorMessage } from 'shared/consts';
import fetchWithAuth from 'shared/services/fetchWithAuth';
import { UploadFile } from 'shared/ui/UploadComponents';
import * as yup from 'yup';
import ShopDotsUploadDotsInfoWidget from './ShopDotsUploadDotsInfoWidget';

const TEMPLATE_URLS = {
  kz: `${BASE_URL}/sales-point/admin/sales-point/admin/sales-points/xlsx_templates/download_kz`,
  kg: `${BASE_URL}/sales-point/admin/sales-point/admin/sales-points/xlsx_templates/download_kg`,
  uz: `${BASE_URL}/sales-point/admin/sales-point/admin/sales-points/xlsx_templates/download_uz`,
} as const;

const validationSchema = yup.object().shape({
  country_id: yup.number().required(requiredErrorMessage),
  file: yup
    .mixed()
    .required('Поле обязательно')
    .test('is-xlsx', 'Файл не поддерживается. Допустимые расширения: xlsx', (value) => {
      if (value === null) {
        return true;
      }
      if (value instanceof File) {
        const fileExtension = value.name.split('.').pop()?.toLowerCase();
        return fileExtension === 'xlsx';
      }
      return false;
    }),
});

const ShopDotsUploadDotsWidget: FC = () => {
  const { data: countryList } = useGetCountriesQuery(null);
  const [uploadShopDotsExcelRequest] = useUploadShopDotsListExcelMutation();
  const [confirmUploadShopDotsExcelRequest] = useConfirmUploadShopDotsListExcelMutation();
  const [infoAboutUpload, setInfoAboutUpload] = useState<InfoAboutUpload | null>(null);
  const [isShowSpinner, setIsShowSpinner] = useState(false);
  const navigate = useNavigate();
  const [isExcelAlreadyLoaded, setIsExcelAlreadyLoaded] = useState(false);

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors, dirtyFields },
    watch,
    ...form
  } = useForm({
    mode: 'all',
    resolver: yupResolver(validationSchema),
  });

  const watchCountryId = watch('country_id');
  const watchFile = watch('file');

  const selectOnChangeCountry = (e: SelectChangeEvent<number | null>) => {
    const value = e.target.value as number;
    setValue?.('country_id', value);
    setIsExcelAlreadyLoaded(false);
  };

  const onSubmit = () => {
    setIsShowSpinner(true);
    uploadShopDotsExcelRequest(form.getValues()).then((res) => {
      if ((res as any).error !== undefined) {
        setIsExcelAlreadyLoaded(true);
      }
      if ((res as any).data && (res as any).data.items.length === 0) {
        setIsExcelAlreadyLoaded(true);
      }
      setIsShowSpinner(false);
      setInfoAboutUpload((res as any).data);
    });
  };

  const handleUploadFile = async (file: File | null, onChange: (file: File | null) => void) => {
    if (watchFile instanceof File) {
      setInfoAboutUpload(null);
    }
    onChange(file);
    setValue?.('file', file as File);
    setIsExcelAlreadyLoaded(false);
  };

  const createShopDots = () => {
    confirmUploadShopDotsExcelRequest(infoAboutUpload?.items).then(() => {
      navigate('/shop-dots');
    });
  };

  const templateButtonText = (key: 'kz' | 'kg' | 'uz') => {
    switch (key) {
      case 'kz':
        return 'Шаблон Казахстан';
      case 'kg':
        return 'Шаблон Кыргызстан';
      case 'uz':
        return 'Шаблон Узбекистан';
    }
  };

  const handleDownloadTemplate = (key: 'kz' | 'kg' | 'uz') => {
    fetchWithAuth(TEMPLATE_URLS[key], { method: 'GET' })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.blob();
      })
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `template_${key}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {
        toast.error('Произошла ошибка при скачивании файла', {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
  };

  return (
    <>
      <Box width={500}>
        <Controller
          name="country_id"
          control={control}
          render={({ field: { onChange, ...restField } }) => (
            <FormControl
              fullWidth
              error={!!errors.country_id}
            >
              <InputLabel id="select-label-type">Страна</InputLabel>
              <Select
                onChange={(e) => selectOnChangeCountry(e)}
                disabled={!!infoAboutUpload && infoAboutUpload.items.length > 0}
                labelId="select-label-type"
                label="Страна"
                {...restField}
              >
                {countryList &&
                  countryList
                    .filter(
                      (country: CountriesType) =>
                        country.id === CountrySelectId.KZ ||
                        country.id === CountrySelectId.KG ||
                        country.id === CountrySelectId.UZ,
                    )
                    .map((country: CountriesType) => (
                      <MenuItem
                        key={country.id}
                        value={country.id}
                      >
                        {country.name}
                      </MenuItem>
                    ))}
              </Select>
              {errors.country_id?.message && <FormHelperText>{errors.country_id.message.toString()}</FormHelperText>}
            </FormControl>
          )}
        />
      </Box>
      <Box width={500}>
        <Controller
          control={control}
          name="file"
          render={({ field: { onChange } }) => (
            <FormControl
              fullWidth
              error={!!errors.country_id}
            >
              <UploadFile
                onChange={(file: File) => handleUploadFile(file, onChange)}
                disabled={(!!infoAboutUpload && infoAboutUpload.items.length > 0) || !watchCountryId}
                textLabel="Загрузить файл"
                helperText="xlsx"
                textButton="Файл"
                errors={errors?.file}
                triggerDialog={!!infoAboutUpload}
                isClearable
                hint="* Вы можете загрузить до 1000 строк"
              />
            </FormControl>
          )}
        />
      </Box>
      <Box
        mt={2}
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
      >
        {Object.keys(TEMPLATE_URLS).map((templateKey) => (
          <Button onClick={() => handleDownloadTemplate(templateKey as 'kz' | 'kg' | 'uz')}>
            {templateButtonText(templateKey as 'kz' | 'kg' | 'uz')}
          </Button>
        ))}
      </Box>
      <Box
        sx={{
          display: 'flex',
          marginTop: '24px',
        }}
      >
        <Button
          disabled={
            !isEmpty(errors) ||
            isEmpty(dirtyFields) ||
            (!!infoAboutUpload && infoAboutUpload.items.length > 0) ||
            isExcelAlreadyLoaded
          }
          variant="contained"
          sx={{ textTransform: 'none' }}
          onClick={handleSubmit(onSubmit)}
        >
          Загрузить EXCEL
        </Button>
      </Box>
      {isShowSpinner && (
        <Box
          mt={2}
          display="flex"
          justifyContent="center"
        >
          <CircularProgress />
        </Box>
      )}
      {infoAboutUpload && (
        <>
          <ShopDotsUploadDotsInfoWidget
            infoAboutUpload={infoAboutUpload}
            countryId={watchCountryId}
          />
          <Box
            display="flex"
            justifyContent="end"
            mt={2}
          >
            <Box
              display="flex"
              alignSelf="end"
            >
              <Button
                onClick={() => navigate('/shop-dots')}
                variant="contained"
                size="large"
                style={{ backgroundColor: '#8C939D', marginRight: '8px' }}
              >
                Отмена
              </Button>
              <Button
                onClick={createShopDots}
                disabled={infoAboutUpload.items.length === 0}
                variant="contained"
                size="large"
              >
                Создать ТТ
              </Button>
            </Box>
          </Box>
        </>
      )}
    </>
  );
};

export default ShopDotsUploadDotsWidget;
